import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Textarea } from '../../../components/ui/textarea';
import { Button } from '../../../components/ui/button';
import AnalyticsService from '../../../services/AnalyticsService/AnalyticsService';

interface CommentAreaProps {
  chatbotId: number;
  messageId: number;
  initialComment?: string | null;
  onCommentChange?: (comment: string | null) => void;
  setError: React.Dispatch<React.SetStateAction<string>>;
}

function CommentArea({
  chatbotId,
  messageId,
  initialComment = null,
  onCommentChange,
  setError
}: Readonly<CommentAreaProps>): React.ReactElement {
  const [comment, setComment] = useState(initialComment || '');
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [isSaving, setIsSaving] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    setComment(initialComment || '');
    setHasUnsavedChanges(false);
  }, [initialComment]);

  useEffect(() => {
    setHighlightedIndex(-1);
  }, [suggestions]);

  const saveComment = useCallback(
    async (text: string): Promise<void> => {
      setIsSaving(true);
      try {
        setError('');
        const result = await AnalyticsService.updateMessageReviewComment(
          chatbotId,
          messageId,
          text.trim() || null
        );
        setHasUnsavedChanges(false);
        if (onCommentChange) {
          onCommentChange(result?.commentText || null);
        }
      } catch (err: unknown) {
        const error = err as { name?: string; apiError?: { message?: string } };
        if (error.name !== 'CanceledError') {
          const message =
            error.apiError?.message ||
            'Failed to save comment. Please try again.';
          setError(message);
        }
      } finally {
        setIsSaving(false);
      }
    },
    [chatbotId, messageId, onCommentChange, setError]
  );

  const fetchSuggestions = useCallback(async (query: string): Promise<void> => {
    if (query.length < 1) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    try {
      const results = await AnalyticsService.getCommentSuggestions(query);
      setSuggestions(results);
      setShowSuggestions(results.length > 0);
    } catch {
      setSuggestions([]);
      setShowSuggestions(false);
    }
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>): void => {
    const newValue = e.target.value;
    setComment(newValue);
    setHasUnsavedChanges(newValue !== (initialComment || ''));
    fetchSuggestions(newValue);
  };

  const handleSuggestionSelect = (suggestion: string): void => {
    setComment(suggestion);
    setShowSuggestions(false);
    setHighlightedIndex(-1);
    saveComment(suggestion);
    textareaRef.current?.focus();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>): void => {
    if (!showSuggestions || suggestions.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex((prev) =>
          prev < suggestions.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex((prev) =>
          prev > 0 ? prev - 1 : suggestions.length - 1
        );
        break;
      case 'Enter':
        if (highlightedIndex >= 0) {
          e.preventDefault();
          handleSuggestionSelect(suggestions[highlightedIndex]);
        }
        break;
      case 'Escape':
        setShowSuggestions(false);
        setHighlightedIndex(-1);
        break;
      default:
        break;
    }
  };

  const handleBlur = (): void => {
    setTimeout(() => {
      setShowSuggestions(false);
      setHighlightedIndex(-1);
    }, 200);
  };

  const handleSave = (): void => {
    saveComment(comment);
  };

  return (
    <div className='relative w-full'>
      <Textarea
        ref={textareaRef}
        value={comment}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
        onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
        placeholder='Add a review comment...'
        className='min-h-[60px] max-h-[200px] text-sm resize-y w-full'
      />
      {showSuggestions && suggestions.length > 0 && (
        <div className='absolute z-10 w-full mt-1 bg-white border border-input rounded-md shadow-md max-h-32 overflow-y-auto'>
          {suggestions.map((suggestion, index) => (
            <button
              key={suggestion}
              type='button'
              className={`w-full px-3 py-2 text-left text-sm truncate ${
                index === highlightedIndex ? 'bg-accent' : 'hover:bg-accent'
              }`}
              onClick={() => handleSuggestionSelect(suggestion)}
              onMouseEnter={() => setHighlightedIndex(index)}
            >
              {suggestion}
            </button>
          ))}
        </div>
      )}
      <div className='mt-2 flex justify-end'>
        <Button
          size='sm'
          onClick={handleSave}
          disabled={!hasUnsavedChanges || isSaving}
        >
          Save
        </Button>
      </div>
    </div>
  );
}

export default CommentArea;
